import type { ApiErrorBody } from '@/domain/types'

// ---------------------------------------------------------------------------
// ApiError — typed error carrying the API's error envelope
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly traceId: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ---------------------------------------------------------------------------
// apiFetch — the single exit point for all API calls
// ---------------------------------------------------------------------------

/**
 * Builds the full URL from VITE_API_BASE_URL, attaches a bearer token,
 * and parses the response. Throws ApiError for non-2xx responses.
 *
 * @param path   Path including leading slash, e.g. `/api/lookup/statuses`
 * @param getToken  Called on every request to acquire a fresh-or-cached token
 * @param init   Standard RequestInit (method, body, etc.)
 */
export async function apiFetch<T>(
  path: string,
  getToken: () => Promise<string>,
  init?: RequestInit,
): Promise<T> {
  const base = import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '')
  const url = `${base}${path}`

  const token = await getToken()

  const response = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  })

  if (!response.ok) {
    let body: ApiErrorBody
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      // Non-JSON error body — synthesise a minimal envelope
      body = {
        code: 'UNKNOWN_ERROR',
        message: `HTTP ${response.status}`,
        traceId: response.headers.get('x-ms-invocation-id') ?? '',
      }
    }
    throw new ApiError(body.code, body.message, body.traceId, response.status, body.details)
  }

  // 204 No Content has no body. The API uses this for DELETE; cast
  // through unknown to satisfy the generic without pretending we have
  // a value.
  if (response.status === 204) {
    return undefined as unknown as T
  }

  return response.json() as Promise<T>
}
